import React, { memo, useCallback, useEffect, useMemo } from 'react';
import { Grow, Typography, Box, createFilterOptions } from '@mui/material';

import { withTranslation } from 'react-i18next';
import { connect, useSelector } from 'react-redux';

import {
  CopyAllRounded,
  CheckOutlined,
  CopyAllOutlined,
} from '@mui/icons-material';
import { teamActions } from '../../../../store/actions/team.actions';
import { clientActions } from '../../../../store/actions/client.actions';

import ClientIcon from '@/shared/assets/icons/sessions/client-session.svg?react';
import AnonymousClientIcon from '@/shared/assets/icons/sessions/anonymous-session.svg?react';

import { useFetchAction } from '../hooks/useFetchAction';
import { useFetchSessionType } from '../hooks/useFetchSessionType';
import { useWizardClientError } from '../hooks/useWizardClientError';

import { useActions } from '../../../../hooks/useActions';

import { classNames } from '../../../../utils';
import { ANON_CLIENT_IDENTIFIER } from '../../../../utils/config';
import translateClientName from '../../../../utils/TranslatedPossibleClientName';

import { InputUi } from '../../../UI/Input/Input';
import { ButtonUi } from '../../../UI/Button/Button';
import CheckboxCard from '../../../UI/CheckboxCard/CheckboxCard';
import AutocompleteUi from '../../../UI/Autocomplete/Autocomplete';
import { WizardContentBox } from '../../../UI/WizardContentBox/WizardContentBox';

import { clientStepStyles } from './style';

const sessionTypes = {
  ANONYMOUS: 'ANONYMOUS',
  CLIENT: 'CLIENT',
};

export const sessionsData = {
  ANONYMOUS: {
    type: sessionTypes.ANONYMOUS,
    checked: false,
    title: 'create-treatment-wizard.select-client.session-type.ANONYMOUS.title',
    desc: 'create-treatment-wizard.select-client.session-type.ANONYMOUS.description',
    Icon: <AnonymousClientIcon />,
  },
  CLIENT: {
    type: sessionTypes.CLIENT,
    checked: false,
    title: 'create-treatment-wizard.select-client.session-type.CLIENT.title',
    desc: 'create-treatment-wizard.select-client.session-type.CLIENT.description',
    Icon: <ClientIcon />,
  },
};

const contentBoxes = {
  auotocomplete: false,
  input: false,
};

function SelectClient(props) {
  const {
    authentication,
    getAllClients,
    clients: clientSelector,
    teams,
    createClient,
    getAllTeams,
    t,
  } = props;
  const { clientSelect } = clientStepStyles;
  const {
    practitioner: { organizationId },
  } = authentication;
  const { clients, isLoading: clientsIsLoading } = clientSelector;

  const {
    canGoNext,
    selectedClient,
    isAnonymousSession,
    anonymousClient,
    resetClientSelect,
    prevClientTreatmentSettings,
  } = useSelector((state) => state.createTreatmentWizard);
  const {
    setClient,
    setNextButton,
    setAnonymousSession,
    setClientReset,
    setClientPrevTreatmentConfigurationsUsage,
  } = useActions();
  const filter = createFilterOptions();

  const [sessionType, setSessionType] = React.useState(sessionsData);
  const [contentBoxActive, setContentBoxActive] = React.useState(contentBoxes);
  const [clientNumber, setClientNumber] = React.useState('');
  const [autocompleteValue, setAutocompleteValue] = React.useState(null);

  const isExistsClient = clients.find((client) => client.name === clientNumber);

  /**
   * Handle Autocomplete change event
   * (option selection, clear value, close...)
   * @param event
   * @param value selected option value
   */
  const handleAutocompleteChange = useCallback(
    (event, value) => {
      setClientNumber(() => '');
      if (!value && selectedClient) {
        setClient(null);
      }
      setClient(clients.find((client) => client.id === value?.value));
      setAutocompleteValue(value);
    },
    [setClient, selectedClient, clients],
  );

  /**
   * Handle Autocomplete`s options render
   * @param optionProps option props
   * @param obj option text and value
   * @returns ReactNode for each Autocomplete option
   */
  const handleAutocompleteRenderOptions = useCallback(
    (optionProps, { text, value }) => (
      <li {...optionProps} key={value}>
        {translateClientName(text)}
      </li>
    ),
    [],
  );

  /**
   * Handle Autocomplete open event
   * @param event
   */
  const handleAutocompleteOpen = useCallback(() => {
    setContentBoxActive(() => ({ input: false, auotocomplete: true }));
  }, [setContentBoxActive]);

  /**
   * Handle Autocomplete serach event
   * @param options Autocomplete options
   * @param params search string
   * @returns filtered Autocomplete options by search
   */
  const handleAutocompleteFilterOption = useCallback(
    (options, params) => {
      const filtered = filter(options, params);
      return filtered;
    },
    [filter],
  );

  /**
   * Handle clients input change event
   * @param value clients input value
   */
  const handleClientInputChange = useCallback(
    (value) => {
      setClientNumber(() => value);
    },
    [setClientNumber],
  );

  /**
   * Handle focus on client input and set it's content box as active
   */
  const handleClientInputFocus = useCallback(() => {
    setContentBoxActive(() => ({ input: true, auotocomplete: false }));
  }, [setContentBoxActive]);

  /**
   * Handle client input btn click and create a new client
   */
  const onCreateClient = useCallback(async () => {
    if (clientNumber.trim()) {
      const actualTeam = teams?.teams[0] || [][0];
      const createdClient = await createClient({
        name: clientNumber,
        teamId: actualTeam?.id,
      });
      if (!createdClient) return;
      setClient(clients.find((client) => client.id === createdClient?.id));
      setAutocompleteValue({
        text: createdClient?.name,
        value: createdClient?.id,
      });
      setClientNumber('');
    }
  }, [
    clientNumber,
    createClient,
    setClient,
    setAutocompleteValue,
    clients,
    teams,
  ]);

  /**
   * Client's Autocomplete option filter
   * @returns filtered clients array
   */
  const sortOptions = useCallback(
    () =>
      clients
        .filter((client) => client.name !== ANON_CLIENT_IDENTIFIER)
        .sort((a, b) =>
          a.name.localeCompare(b.name, undefined, {
            numeric: true,
            sensitivity: 'base',
          }),
        )
        .map((client) => ({ text: client.name, value: client.id })),
    [clients],
  );

  /**
   * Handle session changed
   * @param checkbox selected checkbox data
   */
  const handleSessionTypeSelect = useCallback(
    (checkbox) => {
      switch (checkbox.type) {
        case sessionTypes.ANONYMOUS:
          if (!isAnonymousSession) setAnonymousSession(true);
          setSessionType((prev) => ({
            ANONYMOUS: { ...prev.ANONYMOUS, checked: true },
            CLIENT: { ...prev.CLIENT, checked: false },
          }));
          setClient(anonymousClient || { name: ANON_CLIENT_IDENTIFIER });
          break;
        case sessionTypes.CLIENT:
          setClient(null);
          if (isAnonymousSession) setAnonymousSession(false);
          setSessionType((prev) => ({
            ANONYMOUS: { ...prev.ANONYMOUS, checked: false },
            CLIENT: { ...prev.CLIENT, checked: true },
          }));
          break;
      }
    },
    [isAnonymousSession, setAnonymousSession, setClient, anonymousClient],
  );

  const onSelectExistingClient = useCallback(() => {
    handleAutocompleteChange(null, {
      text: isExistsClient?.name,
      value: isExistsClient?.id,
    });
  }, [handleAutocompleteChange, isExistsClient]);

  const AddClientAdditionalButton = useMemo(() => {
    if (isExistsClient)
      return (
        <Grow in={!!isExistsClient}>
          <Box>
            <ButtonUi
              isIconBtn
              className='select-existing-client'
              size='small'
              tooltipTitle={t(
                'create-treatment-wizard.select-client.create-client-input.client-exists',
              )}
              onClickHandler={onSelectExistingClient}
            >
              <CopyAllRounded />
            </ButtonUi>
          </Box>
        </Grow>
      );
    return null;
  }, [isExistsClient, onSelectExistingClient, t]);

  /**
   * Tracks does client session selected
   */
  const isClientSession = useMemo(
    () => !!sessionType.CLIENT.checked,
    [sessionType],
  );

  const clientsFilter = useMemo(() => {
    const filter = {
      where: {
        organizationId,
        name: { neq: ANON_CLIENT_IDENTIFIER },
      },
    };
    return `filter=${encodeURIComponent(JSON.stringify(filter))}`;
  }, [organizationId]);

  /**
   * Sets session type and selected client if it was previously selected
   * @param setSessionType sectionType setter
   * @param setAutocompleteValue autocompleteValue setter
   */
  useFetchSessionType(setSessionType, setAutocompleteValue);

  /**
   * Fetch all teams
   */
  useFetchAction(getAllTeams);

  /**
   * Fetch all clients
   */
  useFetchAction(getAllClients, clientsFilter);

  /**
   * Set error if client isn't selected
   */
  useWizardClientError(sessionType.ANONYMOUS.checked);

  /**
   * Tracks the use selection to set Next button
   */
  useEffect(() => {
    if (isAnonymousSession) setNextButton(true);
    else if (
      !isAnonymousSession &&
      selectedClient &&
      selectedClient.name !== ANON_CLIENT_IDENTIFIER
    )
      setNextButton(true);
    else setNextButton(false);
  }, [selectedClient?.name, isAnonymousSession]);
  // setNextButton missed in order not to trigger effect all the time
  // selectedClient?.name instead of selectedClient in order not to trigger on new Anon client creation

  const usePrevClientConfig = () => {
    setClientPrevTreatmentConfigurationsUsage();
  };

  useEffect(() => {
    if (resetClientSelect) {
      getAllClients(clientsFilter);
      setAutocompleteValue(null);
      setClientReset(false);
    }
  }, [getAllClients, clientsFilter, resetClientSelect, setClientReset]);

  return (
    <Box sx={clientSelect} className='step-container'>
      <WizardContentBox active>
        <Typography variant='h3' className='wizard-box-title'>
          {t('create-treatment-wizard.select-client.session-type.title')}
        </Typography>
        <Box className='checkbox-card-wrapper'>
          {sessionType &&
            Object.values(sessionType).map((session) => (
              <CheckboxCard
                key={session.type}
                checkboxItem={session}
                onCardSelectHandler={handleSessionTypeSelect}
              />
            ))}
        </Box>
      </WizardContentBox>
      <WizardContentBox
        disabled={!isClientSession}
        active={contentBoxActive.auotocomplete}
      >
        <Typography variant='h3' className='wizard-box-title'>
          {t('create-treatment-wizard.select-client.client-dropdown.title')}
        </Typography>
        <Box className='row-wrapper'>
          <AutocompleteUi
            label={t(
              'create-treatment-wizard.select-client.client-dropdown.label',
            )}
            placeholder={t(
              'create-treatment-wizard.select-client.client-dropdown.placeholder',
            )}
            autocompleteValue={autocompleteValue}
            onAutocompleteChangeHandler={handleAutocompleteChange}
            onAutocompleteOpenHandler={handleAutocompleteOpen}
            onAutocompleteFilterOptionHandler={handleAutocompleteFilterOption}
            onAutocompleteRenderOptionsHandler={handleAutocompleteRenderOptions}
            loading={clientsIsLoading}
            options={sortOptions()}
          />
          <Grow in={!!prevClientTreatmentSettings}>
            <Box>
              <ButtonUi
                isIconBtn
                className={classNames('use-prev-config', {
                  active: prevClientTreatmentSettings?.inUse,
                })}
                disabled={prevClientTreatmentSettings?.inUse}
                size='small'
                tooltipTitle={t(
                  'create-treatment-wizard.select-client.client-dropdown.copy_client_previous_config',
                )}
                tooltipPlacement='top'
                tooltipArrow
                tooltipClassName='prev-config-tooltip'
                onClickHandler={usePrevClientConfig}
              >
                <Grow
                  in={!prevClientTreatmentSettings?.inUse}
                  timeout={{ enter: 800, exit: 0 }}
                  unmountOnExit
                >
                  <CopyAllOutlined />
                </Grow>
                <Grow
                  in={prevClientTreatmentSettings?.inUse}
                  timeout={{ enter: 800, exit: 0 }}
                  unmountOnExit
                >
                  <CheckOutlined />
                </Grow>
              </ButtonUi>
            </Box>
          </Grow>
        </Box>
      </WizardContentBox>
      <WizardContentBox
        disabled={!isClientSession}
        active={contentBoxActive.input}
      >
        <Typography variant='h3' className='wizard-box-title'>
          {t('create-treatment-wizard.select-client.create-client-input.title')}
        </Typography>
        <InputUi
          className='add-client-input'
          label={t(
            'create-treatment-wizard.select-client.create-client-input.label',
          )}
          variant='outlined'
          name='add-client'
          value={clientNumber}
          onInputChangeHandler={handleClientInputChange}
          onInputFocusHandler={handleClientInputFocus}
          endButton
          btnText={t('buttons.treatment-wizard.add')}
          btnDisabled={clientNumber.length <= 0}
          hideBtn={!!isExistsClient}
          btnClickHandler={onCreateClient}
          AdditionalButton={AddClientAdditionalButton}
        />
      </WizardContentBox>
    </Box>
  );
}

const mapStateToProps = (state) => ({
  ...state,
});

const mapDispatchToProps = {
  getAllClients: clientActions.getAllClients,
  createClient: clientActions.createClient,
  getAllTeams: teamActions.getAllTeamsWithoutMeta,
};

export default withTranslation()(
  connect(mapStateToProps, mapDispatchToProps)(memo(SelectClient)),
);
